import { useCallback, useRef, useState } from "react";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import useDemoPlayback from "features/onboarding/guided/useDemoPlayback";
import { SCENE_STEPS } from "features/onboarding/guided/walkthrough/steps";
import { VISUAL_EVENTS, VisualMethod } from "features/onboarding/visuals/types";
import { useOnboardingAnalytics } from "analytics/onboarding";

/**
 * The walkthrough of a bot illustration: the illustration is the
 * clock (see `useLiveClock`) and the active step's bar follows the playhead. Without a `scene`
 * (hedging) it is the plain timer-driven playback.
 */
const useSceneWalkthrough = (
  scene: VisualMethod | null,
  fallbackTotal: number
) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ONBOARDING);
  const track = useOnboardingAnalytics();
  const total = scene ? VISUAL_EVENTS[scene] : fallbackTotal;
  const playback = useDemoPlayback(total, !!scene);
  // The active step's bar follows the illustration's playhead within the step.
  const barRef = useRef<HTMLElement | null>(null);
  const onProgress = useCallback((progress: number) => {
    if (barRef.current) barRef.current.style.transform = `scaleX(${progress})`;
  }, []);
  const steps = scene
    ? SCENE_STEPS[scene].map((step) => ({
        title: t(step.title),
        description: t(step.description),
      }))
    : null;
  // A step chosen from the list: the illustration jumps there (even to the
  // step it is on) and plays on from that point.
  const [seek, setSeek] = useState(0);
  // Did they watch? Each of their own playback actions is recorded.
  const goTo = (next: number): void => {
    track("Walkthrough Interacted", { action: "select_event", stage: next });
    playback.goTo(next);
    if (scene) setSeek((n) => n + 1);
  };
  const advance = (): void => {
    track("Walkthrough Interacted", {
      action: playback.stage >= total ? "replay" : "next_event",
      stage: playback.stage,
    });
    playback.advance();
  };
  const togglePlayback = (): void => {
    track("Walkthrough Interacted", {
      action: playback.playing ? "pause" : "play",
      stage: playback.stage,
    });
    playback.togglePlayback();
  };
  return {
    ...playback,
    goTo,
    advance,
    togglePlayback,
    steps,
    barRef,
    /** What the panel needs to run the illustration (undefined without a scene). */
    panel: scene
      ? {
          running: playback.running,
          seek,
          onStage: playback.setStage,
          onProgress,
        }
      : {},
  };
};

export default useSceneWalkthrough;
